// CampaignSetup.tsx (updated - removed scheduling logic)
import { Controller, useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import {
  FaEnvelope,
  FaInstagram,
  FaSms,
  FaTelegramPlane,
  FaVoicemail,
  FaWeebly,
  FaWhatsapp,
} from "react-icons/fa";
import { RiMessage2Line } from "react-icons/ri";
import { X, Users, Search, PenLine } from "lucide-react";
import { toast } from "sonner";
import UploadTab from "./upload-tab";
import ManualTab from "./manual-tab";
import { CampaignData } from "../../../types";
import { SingleAudienceResponse, SingleCampaignResponse } from "../../../../views/marketing-pages/campaign/types";
import useRequest from "../../../hook/use-req";
import ConvoAIPanel, { ConvoAIMessage } from "../convoai-hub";
import Input from "../../../input";
import Select from "../../../select";
import { ApiAudience, Audience, SenderData } from "../../../../views/marketing-pages/create-campaign/types";
import { applySavedAudienceHeaders } from "../audience-headers";
import { apiErrorMessage } from "../../../../utils/function";

// ─── Types ───────────────────────────────────────────────────────────────────

interface CampaignSetupProps {
  campaignData: CampaignData;
  singleCampaign: SingleCampaignResponse | null;
  setCampaignData: React.Dispatch<React.SetStateAction<CampaignData>>;
  onSubmit: (data: Partial<CampaignData>) => void;
  onValidation: (isValid: boolean) => void;
  audienceById: SingleAudienceResponse | null;
  // Fired on every channel pick; the wizard uses it to hand off to the
  // Instagram path, which has its own steps.
  onChannelChange?: (channel: string) => void;
}

export const CHANNEL_OPTIONS = [
  { value: "sms", label: "SMS", icon: FaSms },
  { value: "whatsapp", label: "WhatsApp", icon: FaWhatsapp },
  { value: "email", label: "Email", icon: FaEnvelope },
  { value: "rcs", label: "RCS", icon: RiMessage2Line },
  { value: "voice", label: "Voice", icon: FaVoicemail },
  { value: "webchat", label: "Web Chat", icon: FaWeebly },
  { value: "telegram", label: "Telegram", icon: FaTelegramPlane },
  { value: "instagram", label: "Instagram", icon: FaInstagram },
];

// RCS Agent type
interface RcsAgent {
  id: string;
  agent_id: string;
  agent_name: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const CampaignSetup: React.FC<CampaignSetupProps> = ({
  campaignData,
  singleCampaign,
  setCampaignData,
  onSubmit,
  onValidation,
  onChannelChange,
  audienceById,
}) => {
  const token = localStorage.getItem("authToken");

  // ── State ──
  const [senders, setSenders] = useState<SenderData[]>([]);
  const [rcsAgents, setRcsAgents] = useState<RcsAgent[]>([]);
  const [loadingRcsAgents, setLoadingRcsAgents] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [, setBrief] = useState("");

  // Target audience
  const [audienceTab, setAudienceTab] = useState<"segments" | "upload" | "manual">("segments");
  const [audienceSearch, setAudienceSearch] = useState("");
  const [allAudiences, setAllAudiences] = useState<Audience[]>([]);
  const selectedAudiences = campaignData.selectedAudiences || [];

  // Upload tab state
  const [uploadAudienceId, setUploadAudienceId] = useState<string | null>(null);
  const [, setUploadedFileName] = useState<string | null>(null);
  const [, setUploadedContacts] = useState<number>(0);
  const [manualConsentChecked, setManualConsentChecked] = useState(false);

  // Add state to track which audience tab has consent requirement
  const [needsConsent, setNeedsConsent] = useState(false);

  // Manual entry state
  const [manualAudienceId, setManualAudienceId] = useState<string | null>(null);
  const [manualValidRecords, setManualValidRecords] = useState<number>(0);

  // Broadcast tags
  const [tagInput, setTagInput] = useState("");
  const tags = campaignData.tags || [];

  // ConvoAI
  const [isConvoOpen, setIsConvoOpen] = useState(true);
  const [isMobileConvoOpen, setIsMobileConvoOpen] = useState(false);

  // ── Requests ──
  const { makeRequest } = useRequest("/campaign", "POST", { Authorization: `Bearer ${token}` });
  const { makeRequest: editCampaign } = useRequest(
    `/campaign/${singleCampaign?.campaign_id}/edit`,
    "PUT",
    { Authorization: `Bearer ${token}` }
  );
  const { makeRequest: senderNames } = useRequest("/senders/all", "GET", {
    Authorization: `Bearer ${token}`,
  });
  const { makeRequest: fetchAudiences } = useRequest("/audience/list", "GET", {
    Authorization: `Bearer ${token}`,
  });
  const { makeRequest: fetchRcsAgents } = useRequest("/rcs/agents/", "GET", {
    Authorization: `Bearer ${token}`,
  });

  // ── Form ──
  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      campaign_name: campaignData.campaign_name || "",
      campaign_goal: campaignData.campaign_goal || "",
      campaign_channel: campaignData.campaign_channel || "",
      sender_name: campaignData.sender_name || "",
      budget: campaignData.budget != null ? String(campaignData.budget) : "",
    },
  });

  const channel = watch("campaign_channel");
  const isEmailCampaign = channel === "email";

  // ── Effects ──
  useEffect(() => {
    const fetchData = async () => {
      const [res] = await senderNames();
      if (res && Array.isArray(res)) setSenders(res);
      else if (res?.data && Array.isArray(res.data)) setSenders(res.data);
    };
    fetchData();
  }, []);

  // Fetch RCS agents when channel is RCS
  useEffect(() => {
    if (channel === "rcs") {
      const loadRcsAgents = async () => {
        setLoadingRcsAgents(true);
        try {
          const [response] = await fetchRcsAgents();
          const agentList = response?.data ?? response ?? [];
          setRcsAgents(Array.isArray(agentList) ? agentList : []);
        } catch (error) {
          console.error("Failed to fetch RCS agents:", error);
        } finally {
          setLoadingRcsAgents(false);
        }
      };
      loadRcsAgents();
    }
  }, [channel]);

  // Fetch audiences from API
  useEffect(() => {
    const fetchAudiencesData = async () => {
      try {
        const [response] = await fetchAudiences();
        if (response && response.audiences && Array.isArray(response.audiences)) {
          const sortedAudiences = response.audiences.sort(
            (a: ApiAudience, b: ApiAudience) => {
              return (
                new Date(b.created_at).getTime() -
                new Date(a.created_at).getTime()
              );
            }
          );

          const transformedAudiences: Audience[] = sortedAudiences.map((audience: ApiAudience) => ({
            id: audience.audience_id,
            audience_id: audience.audience_id,
            name: audience.metadata.unique_file_name.replace(".csv", ""),
            contacts: audience.metadata.unique_count,
            uploadDate: new Date(audience.created_at).toLocaleDateString(),
            status: audience.status,
            validCount: audience.metadata.unique_count,
            invalidCount: audience.metadata.invalid_count,
            duplicateCount: audience.metadata.duplicate_count,
            csvHeaders: audience.csv_headers ?? null,
            firstRowValues: audience.first_row_values ?? [],
            metadata: audience.metadata,
          }));

          setAllAudiences(transformedAudiences);
        }
      } catch (error) {

      }
    };

    fetchAudiencesData();
  }, []);

  useEffect(() => {
    if (singleCampaign) {
      reset({
        campaign_name: singleCampaign.campaign_name || "",
        campaign_channel: singleCampaign.campaign_channel?.toLowerCase() || "",
        sender_name: singleCampaign.sender_name || "",
        campaign_goal: singleCampaign?.campaign_goal || "",
        budget: singleCampaign?.budget != null ? String(singleCampaign.budget) : "",
      });


      if (singleCampaign.broadcast_tags) {
        setCampaignData(prev => ({
          ...prev,
          tags: singleCampaign.broadcast_tags.map((tag: string) =>
            tag.startsWith("#") ? tag : `#${tag}`
          ),
        }));
      }
    }
  }, [singleCampaign, reset]);

  useEffect(() => {
    const ids = getSelectedAudienceIds();

    if (ids.length > 0) {
      // Get the last ID in the array
      const lastId = ids[ids.length - 1];
      localStorage.setItem("audienceId", lastId);
    } else {
      localStorage.removeItem("audienceId");
    }
  }, [selectedAudiences, uploadAudienceId, manualAudienceId]);

  // Saved audiences carry their own csv_headers — write them where the
  // Messaging step's column dropdowns read from. An upload in this session
  // wins (the Upload tab already wrote its own headers + sample row).
  // Only when every selected row came from GET /audience/list (those have
  // `csvHeaders` set, even if null) — Upload/Manual tab entries don't, and
  // uploadAudienceId resets on remount, so this must never overwrite the
  // headers an upload wrote. Nothing selected → leave storage alone.
  useEffect(() => {
    if (uploadAudienceId || selectedAudiences.length === 0 || selectedAudiences.some((a) => a.csvHeaders === undefined)) return;
    applySavedAudienceHeaders(selectedAudiences.map((a) => ({ csv_headers: a.csvHeaders ?? null, first_row_values: a.firstRowValues, metadata: a.metadata as ApiAudience["metadata"] })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAudiences, uploadAudienceId]);

  // Editing a draft: re-select the saved audiences it was built on
  // (GET /campaign/details' `audiences` ids) once the list has loaded, so the
  // checkboxes, counts and CSV headers all come back — only while nothing
  // has been picked yet this session.
  useEffect(() => {
    const ids = singleCampaign?.audiences;
    if (!ids?.length || allAudiences.length === 0 || selectedAudiences.length > 0 || uploadAudienceId || manualAudienceId) return;
    const matched = allAudiences.filter((a) => ids.includes(a.audience_id));
    if (matched.length) setCampaignData((prev) => ({ ...prev, selectedAudiences: matched }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleCampaign, allAudiences]);

  useEffect(() => {
    onValidation(isValid);
  }, [isValid, onValidation]);

  // ── Helpers ──
  const getSenderOptions = () => {
    // For RCS, use agents from /rcs/agents/ endpoint
    if (channel === "rcs") {
      return rcsAgents.map((agent) => ({
        value: agent.agent_id,
        label: agent.agent_name || agent.agent_id,
      }));
    }

    return senders
      .map((s) => {
        let value = "";
        switch (channel) {
          case "sms": value = s.sender_id?.sms; break;
          case "whatsapp": value = s.sender_id.whatsapp; break;
          case "email": value = s.sender_id.email; break;
          case "voice": value = s.sender_id.phone; break;
          default: value = s.sender_id.sms;
        }
        return value ? { value, label: value } : null;
      })
      .filter(Boolean) as { value: string; label: string }[];
  };

  const getSenderLabel = () => {
    switch (channel) {
      case "sms": return "SMS Sender ID";
      case "whatsapp": return "Sender Profile";
      case "email": return "Email Address";
      case "voice": return "Phone Number";
      case "rcs": return "RCS Agent";
      default: return "Sender Profile";
    }
  };



  // Get selected audience IDs for the API payload
  const getSelectedAudienceIds = (): string[] => {
    const ids = [
      ...selectedAudiences.map(a => a.audience_id),
      uploadAudienceId,
      manualAudienceId,
    ].filter(Boolean) as string[];

    return Array.from(new Set(ids));
  };

  // ── Audience handlers ──
  const toggleAudience = (audience: Audience) => {
    setCampaignData(prev => {
      const exists = prev.selectedAudiences?.find(a => a.id === audience.id);

      return {
        ...prev,
        selectedAudiences: exists
          ? (prev.selectedAudiences || []).filter(a => a.id !== audience.id)
          : [...(prev.selectedAudiences || []), audience],
      };
    });
  };

  // Segments are single-select — picking one drops any other previously
  // selected segment first (radio-button behavior), rather than accumulating
  // like the Upload/Manual tabs' entries do. Only affects segment-sourced
  // audiences (identified by membership in `allAudiences`, the fetched
  // saved-audience list) — an already-uploaded file or manual entry stays
  // untouched by this.
  const selectSegment = (audience: Audience) => {
    setCampaignData(prev => {
      const current = prev.selectedAudiences || [];
      const alreadySelected = current.some(a => a.id === audience.id);
      if (alreadySelected) {
        // Clicking the currently-selected segment again deselects it.
        return { ...prev, selectedAudiences: current.filter(a => a.id !== audience.id) };
      }
      const segmentIds = new Set(allAudiences.map(a => a.id));
      const withoutOtherSegments = current.filter(a => !segmentIds.has(a.id));
      return { ...prev, selectedAudiences: [...withoutOtherSegments, audience] };
    });
  };

  // Removes an audience regardless of which tab it was selected from —
  // drops it from the shared selection and, if it happens to be the one
  // currently backing the Upload tab's own file, clears that tab's audience
  // id too so it doesn't silently survive the removal via getSelectedAudienceIds().
  const handleRemoveAudience = (audienceId: string) => {
    const audience = selectedAudiences.find((a) => a.id === audienceId);
    if (audience) toggleAudience(audience);
    if (audienceId === uploadAudienceId) {
      setUploadAudienceId(null);
      setUploadedFileName(null);
      setUploadedContacts(0);
    }
    if (audienceId === manualAudienceId) {
      setManualAudienceId(null);
      setManualValidRecords(0);
    }
  };

  const filteredAudiences = allAudiences.filter((a) =>
    a.name.toLowerCase().includes(audienceSearch.toLowerCase())
  );

  // ── Upload handlers ──
  const handleUploadSuccess = (
    audienceId: string,
    fileName: string,
    contacts: number,
    csvColumns?: string[],
    first_row_values?: string[],
    metadata?: Record<string, any>,
    csvPreviewRow?: Record<string, string>
  ) => {
    setUploadAudienceId(audienceId);
    setUploadedFileName(fileName);
    setUploadedContacts(contacts);

    setCampaignData(prev => ({
      ...prev,
      csvColumns: csvColumns || prev.csvColumns,
      csvPreviewRow: csvPreviewRow || prev.csvPreviewRow,
      selectedAudiences: [
        ...(prev.selectedAudiences || []),
        {
          id: audienceId,
          audience_id: audienceId,
          name: fileName.replace(/\.(csv|xlsx)$/i, ""),
          contacts,
          uploadDate: new Date().toLocaleDateString(),
          status: "completed",
        }
      ]
    }));
  };
  // ── Manual handlers ──
  const handleManualSuccess = (audienceId: string, validRecords: number) => {
    setManualAudienceId(audienceId);
    setManualValidRecords(validRecords);

    setCampaignData(prev => {
      const exists = (prev.selectedAudiences || []).find(a => a.id === audienceId);
      if (exists) return prev;

      return {
        ...prev,
        selectedAudiences: [
          ...(prev.selectedAudiences || []),
          {
            id: audienceId,
            audience_id: audienceId,
            name: `Manual Entry`,
            contacts: validRecords,
            uploadDate: new Date().toLocaleDateString(),
            status: "completed",
          }
        ]
      };
    });
  };

  // ── Tag handlers ──
  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === "Enter" || e.key === " ") && tagInput.trim()) {
      e.preventDefault();

      const tag = tagInput.startsWith("#") ? tagInput : `#${tagInput}`;

      setCampaignData((prev: CampaignData) => {
        const existingTags = prev.tags || [];

        if (existingTags.includes(tag)) return prev;

        return {
          ...prev,
          tags: [...existingTags, tag],
        };
      });

      setTagInput("");
    }
  };

  const removeTag = (tag: string) => {
    setCampaignData((prev: CampaignData) => ({
      ...prev,
      tags: (prev.tags || []).filter((t) => t !== tag),
    }));
  };

  // ── Submit ──
  const buildFormattedData = (data: any) => {
    return {
      campaign_name: data.campaign_name,

      campaign_channel: data.campaign_channel,
      sender_name: data.sender_name,
      audience_ids: getSelectedAudienceIds(),
      broadcast_tags: data?.broadcast_tags || tags.map(tag => tag.replace('#', '')),
      // Optional — only included when the user actually set a budget, since
      // it isn't a required field.
      ...(data.budget !== "" && data.budget != null ? { budget: Number(data.budget) } : {}),
      // No scheduling fields here - they will be added in ScheduleLaunch
    };
  };

  const handleFormSubmit = async (data: any) => {
    setIsSubmitting(true);

    const formattedData = buildFormattedData(data);

    const [response, status] = await makeRequest(formattedData);
    if (status === 201) {
      toast.success("Campaign setup created successfully!");
      const campaignId = response?.response?.campaign_id;
      if (campaignId) localStorage.setItem("campaignId", String(campaignId));

      // Call onSubmit and it will automatically advance to next step
      onSubmit({
        ...formattedData,
        id: campaignId,
      });
    } else {

      toast.error(
        typeof response?.detail === "string"
          ? response.detail
          : JSON.stringify(response?.detail || '"Failed to create campaign"')
      );
      setIsSubmitting(false);
    }

  };

  const handleEditFormSubmit = async (data: any) => {
    setIsSubmitting(true);

    const formattedData = buildFormattedData(data);
    const [response, status] = await editCampaign({
      ...formattedData,
      channel: formattedData.campaign_channel
    });

    if (status === 200) {
      toast.success("Campaign updated successfully!");
      const campaignId = response?.response?.campaign_id;
      if (campaignId) localStorage.setItem("campaignId", String(campaignId));

      onSubmit({
        ...formattedData,
        id: campaignId,
      });
    } else {
      toast.error(apiErrorMessage(response, "Failed to update campaign"));
      setIsSubmitting(false);
    }

  };

  useEffect(() => {
    // Consent is needed only when manual tab is selected AND has contacts
    const isManualTabWithContacts = audienceTab === "manual" && manualValidRecords > 0;
    setNeedsConsent(isManualTabWithContacts);
  }, [audienceTab, manualValidRecords]);


  // ── Convo AI ──
  const handleConvoSend = async (
    message: string,
    _history: ConvoAIMessage[]
  ): Promise<string> => {
    await new Promise((r) => setTimeout(r, 800));
    setBrief(`Campaign: ${message.slice(0, 120)}${message.length > 120 ? "..." : ""}`);
    return "Got it! I've pre-filled the broadcast brief. Tweak it and hit Generate when ready.";
  };

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col lg:flex-row my-10 gap-4 h-full">
      {/* ── Main Form ── */}
      <div className="flex-1 min-w-0 w-full">
        <form
          id="step-1-form"
          onSubmit={handleSubmit(singleCampaign ? handleEditFormSubmit : handleFormSubmit)}
          className="space-y-5"
        >
          {/* Header */}
          <div>
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200">Broadcast Details</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              We have pre-filled this based on your brief. You may edit anything or proceed.
            </p>
          </div>

          {/* Broadcast Name + Channels + Sender Profile — one row, matching design */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Controller
              name="campaign_name"
              control={control}
              rules={{ required: "Campaign name is required" }}
              render={({ field, fieldState }) => (
                <div>
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Broadcast Name</label>
                  <Input
                    type="text"
                    name="name"
                    value={field.value}
                    onChange={(val: any) => field.onChange(val)}
                    error={fieldState?.error?.message}
                    autoComplete="off"
                  />
                </div>
              )}
            />
            <Controller
              name="campaign_channel"
              control={control}
              rules={{ required: "Channel is required" }}
              render={({ field, fieldState }) => (
                <div>
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Channels</label>
                  <Select
                    label=""
                    name="channel"
                    value={field.value}
                    onChange={(val: any) => {
                      field.onChange(val);
                      onChannelChange?.(val);
                    }}
                    error={fieldState?.error?.message}
                    options={CHANNEL_OPTIONS}
                  />
                </div>
              )}
            />
            <Controller
              name="sender_name"
              control={control}
              rules={{ required: "Sender is required" }}
              render={({ field, fieldState }) => (
                <div>
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">{getSenderLabel()}</label>
                  <Select
                    label=""
                    name="sender"
                    value={field.value}
                    onChange={(val: any) => field.onChange(val)}
                    error={fieldState?.error?.message}
                    options={getSenderOptions()}
                  />
                </div>
              )}
            />
          </div>

          {/* Target Audience */}
          <div>
            <label className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-2 block">Target Audience</label>

            {/* Tab switcher — 3 columns */}
            <div className="grid grid-cols-1 sm:grid-cols-3 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 mb-3">
              <button
                type="button"
                onClick={() => setAudienceTab("segments")}
                className={`flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors border-r border-gray-200 dark:border-gray-700 ${audienceTab === "segments"
                  ? "bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                  : "bg-[#EAEDFB] text-gray-500 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-700"
                  }`}
              >
                <Users size={15} />
                Saved Audiences
              </button>
              <button
                type="button"
                onClick={() => setAudienceTab("upload")}
                className={`flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors border-r border-gray-200 dark:border-gray-700 ${audienceTab === "upload"
                  ? "bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                  : "bg-[#EAEDFB] text-gray-500 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-700"
                  }`}
              >
                <Users size={15} />
                Upload List
              </button>
              <button
                type="button"
                onClick={() => setAudienceTab("manual")}
                className={`flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors ${audienceTab === "manual"
                  ? "bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                  : "bg-[#EAEDFB] text-gray-500 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-700"
                  }`}
              >
                <PenLine size={15} />
                Enter Manually
              </button>
            </div>


            {/* ── Saved Audiences tab ── */}
            {audienceTab === "segments" && (
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7B7B7B] font-semibold dark:text-gray-500" />
                  <input
                    type="text"
                    placeholder="Search saved audiences by name..."
                    value={audienceSearch}
                    onChange={(e) => setAudienceSearch(e.target.value)}
                    className="w-full pl-9 pr-4 py-2.5 text-sm border-b border-gray-200 dark:border-gray-700 outline-none focus:bg-gray-50 dark:focus:bg-gray-800 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                  />
                </div>
                <div className="max-h-48 overflow-y-auto divide-y divide-gray-100">
                  {filteredAudiences.length === 0 ? (
                    <p className="text-center text-sm text-[#7B7B7B] font-semibold dark:text-gray-500 py-6">No saved audiences found</p>
                  ) : (
                    filteredAudiences.map((audience) => {
                      const isChecked = !!(selectedAudiences || []).find((a) => a.id === audience.id);
                      return (
                        <label
                          key={audience.id}
                          // Handled here rather than the radio's onChange —
                          // native radios don't fire a change event when you
                          // click one that's already checked, which would've
                          // made "click again to deselect" unreachable.
                          onClick={(e) => { e.preventDefault(); selectSegment(audience); }}
                          className={`flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${isChecked ? "bg-green-50/40" : ""}`}
                        >
                          <input
                            type="radio"
                            name="selected-segment"
                            checked={isChecked}
                            readOnly
                            className="mt-0.5 accent-green-600 w-4 h-4 cursor-pointer"
                          />
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium ${isChecked ? "text-green-700 dark:text-green-400" : "text-gray-700 dark:text-gray-300"}`}>
                              📁 {audience.name}
                            </p>
                            <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 truncate">
                              Uploaded: {audience.uploadDate}
                            </p>
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400 font-medium whitespace-nowrap">
                            {audience.contacts?.toLocaleString()}<br />
                            <span className="text-[#7B7B7B] font-semibold dark:text-gray-500">Contacts</span>
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>
                {(selectedAudiences || []).length > 0 && (() => {
                  // Only sums audiences that actually carry these counts
                  // (segment rows do, via GET /audience/list's metadata —
                  // upload/manual entries don't set them here since those
                  // tabs already show their own live breakdown).
                  const withCounts = selectedAudiences.filter((a) => a.validCount != null);
                  const valid = withCounts.reduce((sum, a) => sum + (a.validCount || 0), 0);
                  const invalid = withCounts.reduce((sum, a) => sum + (a.invalidCount || 0), 0);
                  const duplicates = withCounts.reduce((sum, a) => sum + (a.duplicateCount || 0), 0);
                  return (
                    <div className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between flex-wrap gap-2">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {selectedAudiences.length} audience{selectedAudiences.length > 1 ? "s" : ""} selected
                      </span>
                      {withCounts.length > 0 && (
                        <div className="flex items-center gap-0 text-xs text-gray-500 dark:text-gray-400 flex-wrap">
                          <span className="flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                            Valid <strong className="text-green-600 dark:text-green-400 ml-0.5">{valid}</strong>
                          </span>
                          <span className="mx-2 text-gray-300 dark:text-gray-600">|</span>
                          <span className="flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500 inline-block" />
                            Invalid <strong className="text-red-600 dark:text-red-400 ml-0.5">{invalid}</strong>
                          </span>
                          <span className="mx-2 text-gray-300 dark:text-gray-600">|</span>
                          <span className="flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 inline-block" />
                            Duplicates <strong className="text-blue-600 dark:text-blue-400 ml-0.5">{duplicates}</strong>
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* ── Upload tab ── */}
            {audienceTab === "upload" && (
              <UploadTab
                channel={channel}
                onUploadSuccess={handleUploadSuccess}
                audienceById={audienceById}
                onValidation={onValidation}
                selectedAudiences={selectedAudiences}
                onRemoveAudience={handleRemoveAudience}
              />
            )}

            {/* ── Manual tab ── */}
            {audienceTab === "manual" && (
              <ManualTab
                channel={channel}
                isEmailCampaign={isEmailCampaign}
                onManualSuccess={handleManualSuccess}
                audienceById={audienceById}
                campaignData={campaignData}
                setCampaignData={setCampaignData}
                onValidation={onValidation}
                onConsentChange={setManualConsentChecked}
              />
            )}
          </div>

          {/* Broadcast Tags */}
          <div>
            <label className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Broadcast Tags</label>
            <div className="flex flex-wrap items-center gap-2 min-h-[44px] px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 focus-within:ring-2 focus-within:ring-blue-200 focus-within:border-blue-400 transition-all">
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded px-2 py-0.5"
                >
                  {tag}
                  <button type="button" onClick={() => removeTag(tag)} className="hover:text-gray-900 dark:hover:text-gray-100">
                    <X size={10} />
                  </button>
                </span>
              ))}
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
                placeholder={tags.length === 0 ? "Type a tag and press Enter..." : ""}
                className="flex-1 min-w-[120px] outline-none text-sm bg-transparent"
              />
            </div>
          </div>

          {/* Budget — optional spend limit, not required to proceed */}
          <Controller
            name="budget"
            control={control}
            render={({ field, fieldState }) => (
              <div>
                <label className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">
                  Your Budget Set spend limits to new reach
                </label>
                <Input
                  type="number"
                  name="budget"
                  min={0}
                  placeholder="e.g. $500"
                  value={field.value}
                  onChange={(val: any) => field.onChange(val)}
                  error={fieldState?.error?.message}
                  subtext="Set spend limits to view reach"
                  autoComplete="off"
                />
              </div>
            )}
          />

          {/* Navigation */}
          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={
                isSubmitting ||
                ((selectedAudiences || []).length === 0 && !uploadAudienceId && !manualAudienceId) ||
                (needsConsent && !manualConsentChecked) // Require consent only for manual tab with contacts
              }
              className="px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? "Saving..." : "Continue to Message"}
            </button>
          </div>
        </form>
      </div>

      {/* ── ConvoAI Side Panel (desktop) ── */}
      <aside className="hidden lg:flex w-80 shrink-0 border-l border-slate-200 dark:border-slate-700 flex-col">
        <ConvoAIPanel
          isOpen={isConvoOpen}
          onClose={() => setIsConvoOpen(false)}
          onSend={handleConvoSend}
        />

        {!isConvoOpen && (
          <div className="flex items-center justify-center h-full">
            <button
              onClick={() => setIsConvoOpen(true)}
              className="flex items-center gap-2 text-violet-600 border border-violet-200 hover:bg-violet-50 px-4 py-2 rounded-xl text-sm font-medium transition-colors"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Open ConvoAI
            </button>
          </div>
        )}
      </aside>

      {/* ── Mobile FAB ── */}
      <button
        onClick={() => setIsMobileConvoOpen(true)}
        className="lg:hidden fixed bottom-6 right-6 z-50 w-14 h-14 bg-violet-600 hover:bg-violet-700 text-white rounded-full shadow-lg shadow-violet-200 flex items-center justify-center transition-transform hover:scale-105 active:scale-95"
        aria-label="Open ConvoAI"
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* ── Mobile Drawer ── */}
      {isMobileConvoOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/30 backdrop-blur-sm"
            onClick={() => setIsMobileConvoOpen(false)}
          />
          <div className="absolute bottom-0 left-0 right-0 h-[75vh] bg-white dark:bg-gray-900 rounded-t-2xl overflow-hidden flex flex-col shadow-2xl">
            <ConvoAIPanel
              isOpen={true}
              onClose={() => setIsMobileConvoOpen(false)}
              onSend={handleConvoSend}
            />
          </div>
        </div>
      )}
    </div>
  );
};
